import { BlockManager } from "../BlockManager";
import { ElementType } from "@easy-email-pro-core/constants/blockType";
import type {
  BasicElement,
  Element,
  TextNode,
} from "@easy-email-pro-core/typings";
import { IBlockData, IPage, INavbar, ISocial } from "./type";
import {
  cloneDeep,
  get,
  identity,
  isUndefined,
  omit,
  pickBy,
  set,
} from "lodash";
import { HtmlNodeAdapter } from "./HtmlNodeAdapter";
import { ElementCategory } from "@easy-email-pro-core/constants";

// 基础组件
enum BasicType {
  PAGE = "page",
  SECTION = "section",
  COLUMN = "column",
  GROUP = "group",
  TEXT = "text",
  IMAGE = "image",
  DIVIDER = "divider",
  SPACER = "spacer",
  BUTTON = "button",
  WRAPPER = "wrapper",
  RAW = "raw",
  ACCORDION = "accordion",
  ACCORDION_ELEMENT = "accordion-element",
  ACCORDION_TITLE = "accordion-title",
  ACCORDION_TEXT = "accordion-text",
  HERO = "hero",
  CAROUSEL = "carousel",
  NAVBAR = "navbar",
  SOCIAL = "social",
  // TODO
  TABLE = "table",

  TEMPLATE = "template",
}

// 高级组件
enum AdvancedType {
  TEXT = "advanced_text",
  IMAGE = "advanced_image",
  DIVIDER = "advanced_divider",
  SPACER = "advanced_spacer",
  BUTTON = "advanced_button",
  NAVBAR = "advanced_navbar",
  SOCIAL = "advanced_social",
  ACCORDION = "advanced_accordion",
  CAROUSEL = "advanced_carousel",

  WRAPPER = "advanced_wrapper",
  SECTION = "advanced_section",
  COLUMN = "advanced_column",
  GROUP = "advanced_group",
  HERO = "advanced_hero",
}

const easyEmailBlockMap: Record<string, Element["type"]> = {
  [BasicType.TEXT]: ElementType.STANDARD_PARAGRAPH,
  [BasicType.BUTTON]: ElementType.STANDARD_BUTTON,
  [BasicType.IMAGE]: ElementType.STANDARD_IMAGE,
  [BasicType.DIVIDER]: ElementType.STANDARD_DIVIDER,
  [BasicType.SPACER]: ElementType.STANDARD_SPACER,
  [BasicType.SOCIAL]: ElementType.STANDARD_SOCIAL,
  [BasicType.NAVBAR]: ElementType.STANDARD_NAVBAR,
  [BasicType.WRAPPER]: ElementType.STANDARD_WRAPPER,
  [BasicType.SECTION]: ElementType.STANDARD_SECTION,
  [BasicType.GROUP]: ElementType.STANDARD_GROUP,
  [BasicType.COLUMN]: ElementType.STANDARD_COLUMN,
  [AdvancedType.TEXT]: ElementType.STANDARD_TEXT,
  [AdvancedType.BUTTON]: ElementType.STANDARD_BUTTON,
  [AdvancedType.IMAGE]: ElementType.STANDARD_IMAGE,
  [AdvancedType.DIVIDER]: ElementType.STANDARD_DIVIDER,
  [AdvancedType.HERO]: ElementType.STANDARD_HERO,
  [AdvancedType.SPACER]: ElementType.STANDARD_SPACER,
  [AdvancedType.SOCIAL]: ElementType.STANDARD_SOCIAL,
  [AdvancedType.NAVBAR]: ElementType.STANDARD_NAVBAR,
  [AdvancedType.WRAPPER]: ElementType.STANDARD_WRAPPER,
  [AdvancedType.SECTION]: ElementType.STANDARD_SECTION,
  [AdvancedType.GROUP]: ElementType.STANDARD_GROUP,
  [AdvancedType.COLUMN]: ElementType.STANDARD_COLUMN,
};

export { HtmlNodeAdapter };

export const easyEmailToEasyEmailPro = (sourceElement: IPage) => {
  const element = cloneDeep(sourceElement);
  const globalAttributes: Record<string, string> = {};
  const categoryAttributes: Record<string, Record<string, string>> = {
    [ElementCategory.TEXT]: {},
  };
  if (element.data.value["font-family"]) {
    globalAttributes["font-family"] = element.data.value["font-family"];
  }
  if (element.data.value["font-size"]) {
    categoryAttributes[ElementCategory.TEXT] = {
      ...categoryAttributes[ElementCategory.TEXT],
      "font-size": element.data.value["font-size"],
    };
  }
  if (element.data.value["line-height"]) {
    categoryAttributes[ElementCategory.TEXT] = {
      ...categoryAttributes[ElementCategory.TEXT],
      "line-height": element.data.value["line-height"],
    };
  }
  if (element.data.value["font-weight"]) {
    categoryAttributes[ElementCategory.TEXT] = {
      ...categoryAttributes[ElementCategory.TEXT],
      "font-weight": element.data.value["font-weight"],
    };
  }
  if (element.data.value["text-color"]) {
    categoryAttributes[ElementCategory.TEXT] = {
      ...categoryAttributes[ElementCategory.TEXT],
      color: element.data.value["text-color"],
    };
  }
  if (element.data.value["font-weight"]) {
    globalAttributes["font-weight"] = element.data.value["font-weight"];
  }

  return BlockManager.getBlockByType(ElementType.PAGE).create({
    attributes: {
      width: element.attributes.width,
      "background-color": element.attributes["background-color"],
      "content-background-color":
        element.data.value["content-background-color"],
      "margin-top": "0px",
      "margin-bottom": "0px",
    },
    data: {
      globalAttributes: globalAttributes,
      categoryAttributes: categoryAttributes,
      blockAttributes: {},
      classAttributes: {},
      breakpoint: element.data.value.breakpoint,
      headStyles: element.data.value.headStyles,
      fonts: element.data.value.fonts,
    },
    children: element.children.map((item) => loop(item, sourceElement)),
  });
};

const loop = (
  element: IBlockData,
  parentElement: IBlockData | null
): Element => {
  let children: Array<Element | TextNode> = element.children.map((item) =>
    loop(item, element)
  );
  if (element.data.value.content) {
    children = HtmlNodeAdapter(element.data.value.content);
  }

  if (
    element.type === BasicType.NAVBAR ||
    element.type === AdvancedType.NAVBAR
  ) {
    const navbar = element as INavbar;
    children = navbar.data.value.links.map((item) => {
      const childList = HtmlNodeAdapter(item.content);
      return BlockManager.getBlockByType(
        ElementType.STANDARD_NAVBAR_LINK
      ).create({
        attributes: {
          ...omit(item, "content"),
        },
        children: childList.length === 0 ? [{ text: "" }] : childList,
      });
    });
  }
  if (
    element.type === BasicType.SOCIAL ||
    element.type === AdvancedType.SOCIAL
  ) {
    const social = element as ISocial;
    children = social.data.value.elements.map((item) => {
      const childList = HtmlNodeAdapter(item.content);
      return BlockManager.getBlockByType(
        ElementType.STANDARD_SOCIAL_ELEMENT
      ).create({
        attributes: {
          ...omit(item, "content"),
        },
        children: childList.length === 0 ? [{ text: "" }] : childList,
      });
    });
  }

  if (children.length === 0) {
    children = [{ text: "" }];
  }

  let visible: "desktop" | "mobile" | undefined = undefined;
  if (element.displayMode === "desktop") {
    visible = "desktop";
  } else if (element.displayMode === "mobile") {
    visible = "mobile";
  }

  let logic: BasicElement["logic"] = undefined;
  if (element.data.value?.condition) {
    if (!logic) {
      logic = {};
    }
    logic.condition = element.data.value?.condition;
  }
  if (element.data.value?.iteration) {
    if (!logic) {
      logic = {};
    }
    logic.iteration = element.data.value?.iteration;
  }

  const standardElement = {
    type: easyEmailBlockMap[element.type] || (element.type as any),
    attributes: pickBy(element.attributes, (v) => {
      return !isUndefined(v) && v.trim() !== "";
    }),
    mobileAttributes: element.mobileAttributes
      ? pickBy(element.mobileAttributes, (v) => {
          return !isUndefined(v) && v.trim() !== "";
        })
      : undefined,
    data: omit(element.data.value, "content"),
    children: children,
    visible,
    logic: logic,
  } as BasicElement;

  const padding = get(standardElement.attributes, "padding");
  const innerPadding = get(standardElement.attributes, "inner-padding");
  if (padding) {
    const div = document.createElement("div");
    div.style.setProperty("padding", padding);
    set(standardElement.attributes, "padding-top", div.style.paddingTop);
    set(standardElement.attributes, "padding-bottom", div.style.paddingBottom);
    set(standardElement.attributes, "padding-left", div.style.paddingLeft);
    set(standardElement.attributes, "padding-right", div.style.paddingRight);
  }

  if (innerPadding) {
    const div = document.createElement("div");
    div.style.setProperty("padding", innerPadding);
    set(standardElement.attributes, "inner-padding-top", div.style.paddingTop);
    set(
      standardElement.attributes,
      "inner-padding-bottom",
      div.style.paddingBottom
    );
    set(
      standardElement.attributes,
      "inner-padding-left",
      div.style.paddingLeft
    );
    set(
      standardElement.attributes,
      "inner-padding-right",
      div.style.paddingRight
    );
  }

  if (
    (
      [ElementType.STANDARD_HERO, ElementType.STANDARD_SECTION] as string[]
    ).includes(standardElement.type)
  ) {
    standardElement.attributes["background-image-enabled"] = true;
  }
  if (
    (
      [
        ElementType.STANDARD_BUTTON,
        ElementType.STANDARD_IMAGE,
        ElementType.STANDARD_SECTION,
      ] as string[]
    ).includes(standardElement.type)
  ) {
    const div = document.createElement("div");
    div.style.setProperty("border", standardElement.attributes["border"]);

    if (
      (standardElement.attributes["border"] &&
        standardElement.attributes["border"].trim() !== "none") ||
      standardElement.attributes["border-width"]
    ) {
      standardElement.attributes["border-width"] =
        div.style.getPropertyValue("border-width");
      standardElement.attributes["border-style"] =
        div.style.getPropertyValue("border-style");
      standardElement.attributes["border-color"] =
        div.style.getPropertyValue("border-color");
      standardElement.attributes["border-enabled"] = true;
      delete standardElement.attributes["border"];
    }
  }

  const block = pickBy(standardElement, identity);

  if (
    [
      AdvancedType.TEXT,
      AdvancedType.IMAGE,
      AdvancedType.DIVIDER,
      AdvancedType.SPACER,
      AdvancedType.BUTTON,
      AdvancedType.NAVBAR,
      AdvancedType.SOCIAL,
      AdvancedType.SOCIAL,
    ].includes(element.type as any) &&
    parentElement &&
    !parentElement.type.includes("column")
  ) {
    return BlockManager.getBlockByType(ElementType.STANDARD_SECTION).create({
      attributes: {
        "padding-top": "0px",
        "padding-bottom": "0px",
        "padding-left": "0px",
        "padding-right": "0px",
      },
      children: [
        BlockManager.getBlockByType(ElementType.STANDARD_COLUMN).create({
          children: [block as Element],
        }),
      ],
    });
  }
  return block as Element;
};
